import { useEffect, useState } from "react";
import type { TaskStats, TaskTradeTotals } from "../api/types";

export type TaskExecutionEvent = {
  id: number;
  taskId: string;
  side: "buy" | "sell";
  sizeSol: number;
  sizeUsd: number;
  dex: string;
  pool: string;
  wallet: string;
  signature: string | null;
  stage1Signature?: string | null;
  stage2Signature?: string | null;
  executionStatus?: string | null;
  recoveryToken?: string | null;
  recoveryAmount?: string | null;
  realizedOutputAsset?: string | null;
  realizedOutputRaw?: string | null;
  realizedOutputDecimals?: number | null;
  realizedOutputUsd?: number | null;
  bundleId?: string | null;
  batchId?: string | null;
  legIndex?: number | null;
  legCount?: number | null;
  path: string | null;
  landed: boolean;
  dryRun: boolean;
  ts: number;
};

/**
 * Subscribe to SSE for a single task. Returns the latest stats snapshot
 * and the most recent execution events (last 20). Trade totals cover all history.
 */
export function useTaskStream(taskId: string | null): {
  stats: TaskStats | null;
  tradeTotals: TaskTradeTotals | null;
  executions: TaskExecutionEvent[];
  error: string | null;
} {
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [tradeTotals, setTradeTotals] = useState<TaskTradeTotals | null>(null);
  const [executions, setExecutions] = useState<TaskExecutionEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) {
      setExecutions([]);
      setStats(null);
      setTradeTotals(null);
      setError(null);
      return;
    }
    // Reset state when switching between tasks so the previous task's list
    // doesn't briefly bleed into the new one while the fetch is in flight.
    setExecutions([]);
    setStats(null);
    setTradeTotals(null);
    setError(null);

    // Hydrate executions from persistent DB on mount — SSE only delivers
    // FUTURE events, so without this the list is empty after every page
    // reload until new trades happen. GET /api/tasks/:id returns up to 50
    // most-recent executions.
    let cancelled = false;
    let receivedLiveTotals = false;
    fetch(`/api/tasks/${taskId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        // An older HTTP snapshot must not overwrite totals that arrived via SSE.
        if (!receivedLiveTotals && data.task?.tradeTotals) {
          setTradeTotals(data.task.tradeTotals as TaskTradeTotals);
        }
        if (!Array.isArray(data.executions)) return;
        // DB rows → TaskExecutionEvent shape. DB uses different field names
        // (our_side, our_size_sol, etc) than the SSE event payload.
        type ExecutionRow = {
          id: number;
          task_id: string;
          our_side: "buy" | "sell";
          our_size_sol: number;
          our_size_usd: number;
          dex: string;
          wallet_label: string;
          signature: string | null;
          stage1_signature: string | null;
          stage2_signature: string | null;
          execution_status: string | null;
          recovery_token: string | null;
          recovery_amount: string | null;
          realized_output_asset: string | null;
          realized_output_raw: string | null;
          realized_output_decimals: number | null;
          realized_output_usd: number | null;
          bundle_id: string | null;
          batch_id: string | null;
          leg_index: number | null;
          leg_count: number | null;
          path: string | null;
          landed: 0 | 1;
          dry_run: 0 | 1;
          created_at: number;
        };
        const initial: TaskExecutionEvent[] = (data.executions as ExecutionRow[]).map(
          (r) => ({
            id: r.id,
            taskId: r.task_id,
            side: r.our_side,
            sizeSol: r.our_size_sol,
            sizeUsd: r.our_size_usd,
            dex: r.dex,
            pool: "",
            wallet: r.wallet_label,
            signature: r.signature,
            stage1Signature: r.stage1_signature,
            stage2Signature: r.stage2_signature,
            executionStatus: r.execution_status,
            recoveryToken: r.recovery_token,
            recoveryAmount: r.recovery_amount,
            realizedOutputAsset: r.realized_output_asset,
            realizedOutputRaw: r.realized_output_raw,
            realizedOutputDecimals: r.realized_output_decimals,
            realizedOutputUsd: r.realized_output_usd,
            bundleId: r.bundle_id,
            batchId: r.batch_id,
            legIndex: r.leg_index,
            legCount: r.leg_count,
            path: r.path,
            landed: r.landed === 1,
            dryRun: r.dry_run === 1,
            ts: r.created_at,
          }),
        );
        setExecutions((prev) => {
          // Preserve any SSE events that beat the fetch (same-id takes live version).
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...prev, ...initial.filter((e) => !seen.has(e.id))];
          merged.sort((a, b) => b.ts - a.ts);
          return merged.slice(0, 20);
        });
      })
      .catch(() => {
        // non-fatal — SSE will populate as new executions arrive
      });

    const src = new EventSource(`/api/tasks/${taskId}/stream`);
    src.addEventListener("totals", (ev) => {
      if (cancelled) return;
      try {
        const totals = JSON.parse((ev as MessageEvent).data) as TaskTradeTotals;
        receivedLiveTotals = true;
        setTradeTotals(totals);
      } catch {
        // ignore
      }
    });
    src.addEventListener("stats", (ev) => {
      try {
        setStats(JSON.parse((ev as MessageEvent).data) as TaskStats);
      } catch {
        // ignore
      }
    });
    src.addEventListener("execution", (ev) => {
      try {
        const e = JSON.parse((ev as MessageEvent).data) as TaskExecutionEvent;
        setExecutions((prev) => {
          // update in place if id matches (landing update), else prepend
          const idx = prev.findIndex((p) => p.id === e.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = e;
            return next;
          }
          return [e, ...prev].slice(0, 20);
        });
      } catch {
        // ignore
      }
    });
    src.addEventListener("error", (ev) => {
      try {
        const m = ev as MessageEvent;
        if (m.data) setError(String(m.data));
      } catch {
        // ignore
      }
    });
    src.onerror = (): void => {
      // auto-reconnect handled by EventSource
    };
    return (): void => {
      cancelled = true;
      src.close();
    };
  }, [taskId]);

  return { stats, tradeTotals, executions, error };
}
